import { EpicIcons, InputText } from "@constellation-academy/epic-ui";
import { useRef, useState } from "react";
import { useMutation } from "react-relay";
import { useTranslation } from "@i18n/hooks";
import { s3VideoSourceField_GetS3VideoDataV2Mutation } from "@relay/s3VideoSourceField_GetS3VideoDataV2Mutation.graphql";
import { GET_S3_VIDEO_DATA_MUTATION } from "./s3-video-source-field.graphql";
import {
	errorTextClass,
	inputWrapperClass,
	previewVideoClass,
	videoDisplayWrapper,
	warningTextClass,
} from "./s3-video-source-field.styles";
import { S3VideoDataState, S3VideoSourceFieldProps } from "./s3-video-source-field.types";
import {
	formatSizeInMegabytes,
	getS3FolderErrorTranslationKey,
	getSkippedSubtitleTranslation,
} from "./s3-video-source-field.utils";
import { Button } from "../../../../components/button";
import { P2Span } from "../../../../themes/font-tags";

export const S3VideoSourceField = ({
	folder,
	videoElementId,
	onUpdate,
	onChange,
}: S3VideoSourceFieldProps) => {
	const { t } = useTranslation("common");
	const [getS3VideoData, isGettingS3VideoData] =
		useMutation<s3VideoSourceField_GetS3VideoDataV2Mutation>(GET_S3_VIDEO_DATA_MUTATION);

	const [videoData, setVideoData] = useState<S3VideoDataState>();
	const [error, setError] = useState<string>();

	const trimmedFolder = folder?.trim() ?? "";

	const currentFolderRef = useRef(trimmedFolder);
	currentFolderRef.current = trimmedFolder;

	const handleGetS3VideoDataOnClick = () => {
		setError(undefined);
		setVideoData(undefined);

		const checkedFolder = trimmedFolder;
		const isStillCurrentFolder = () => currentFolderRef.current === checkedFolder;

		getS3VideoData({
			variables: { input: { folder: checkedFolder, videoElementId } },
			onCompleted: (response) => {
				if (!isStillCurrentFolder()) return;

				const responseVideoData = response.Admin.ElearningV2.getS3VideoData?.s3VideoData;

				if (!responseVideoData) {
					setError(t("educationalOffer.s3VideoNotFound", { folder: checkedFolder }));
					return;
				}

				setVideoData({ ...responseVideoData });
			},
			onError: (mutationError) => {
				if (!isStillCurrentFolder()) return;

				setError(
					t(getS3FolderErrorTranslationKey(mutationError), { folder: checkedFolder }),
				);
			},
		});
	};

	const handleInputOnChange = (input: string | undefined) => {
		setVideoData(undefined);
		setError(undefined);
		onUpdate(input);
		onChange?.();
	};

	return (
		<div>
			<div className={inputWrapperClass}>
				<InputText
					placeholder={t("educationalOffer.s3FolderPlaceholder")}
					id="folder"
					value={folder}
					onChange={handleInputOnChange}
				/>
				<Button
					icon={EpicIcons.REFRESH}
					disabled={isGettingS3VideoData}
					onClick={handleGetS3VideoDataOnClick}
				/>
			</div>
			{videoData && (
				<div className={videoDisplayWrapper}>
					<div>{t("educationalOffer.s3VideoKey", { key: videoData.videoKey })}</div>
					<div>
						{t("educationalOffer.s3VideoSize", {
							size: formatSizeInMegabytes(videoData.sizeInBytes),
						})}
					</div>
					<div>
						{videoData.subtitleLanguages.length > 0
							? t("educationalOffer.s3VideoSubtitles", {
									languages: videoData.subtitleLanguages.join(", "),
							  })
							: t("educationalOffer.s3VideoNoSubtitles")}
					</div>
					{videoData.invalidSubtitles.map((invalidSubtitle) => {
						const { translationKey, options } =
							getSkippedSubtitleTranslation(invalidSubtitle);

						return (
							<P2Span key={invalidSubtitle.language} className={warningTextClass}>
								{t(translationKey, options)}
							</P2Span>
						);
					})}
					<video
						className={previewVideoClass}
						height={200}
						controls
						src={videoData.playbackUrl}
					/>
				</div>
			)}
			{error && <P2Span className={errorTextClass}>{error}</P2Span>}
		</div>
	);
};
